import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Map, Marker, ZoomControl, Overlay } from "pigeon-maps";
import SafeGeoJsonLoader from "./SafeGeoJsonLoader";
import { useStaticQuery, graphql, navigate } from "gatsby";
import { useLocation as useRouterLocation } from "@reach/router";
import MapStyleSwitcher from "./MapStyleSwitcher"; 
import CountryFlagMarker from "./CountryFlagMarker"; // Import the new marker component
import PointMarker from "./PointMarker"; // Import PointMarker component
import PostMarker from "./PostMarker"; // Import PostMarker component
import HighlightMarker from "./HighlightMarker";
import HighlightRiverOverlay from "./HighlightRiverOverlay";
import MapTileOverlay from "./MapTileOverlay";
import CountryShapesOverlay from "./CountryShapesOverlay";
import RegionShapesOverlay from "./RegionShapesOverlay";
import ExploreRadiusOverlay from "./ExploreRadiusOverlay";
import { useLocation } from "../../context/LocationContext";

import { buildCategoryPath } from "../../utils/path-utils";
import { hasFlagAsset } from "../../utils/flag-assets";
import {
  getEuropeHighlights,
  getCuratedEuropeHighlights,
  HIGHLIGHT_ACTIVITIES,
  resolveHighlightRegionSlug,
  slugifyRegion,
  isLogisticsHubHighlight,
  type HighlightActivity,
} from "../../utils/europe-highlights";
import {
  highlightMatchesDifficulty,
  parseDifficultyQuery,
} from "../../utils/activity-difficulty";
import {
  getWpHighlightsForActivities,
  pointMatchesActivity,
  type WpActivityPoint,
} from "../../utils/activity-browse-points";
import {
  anchorInBounds,
  padBounds,
} from "../../utils/map-viewport";
import { useSettledMapView } from "./useSettledMapView";
import { WORLD_COUNTRIES } from "../../data/world-countries";
import regionIndex from "../../data/world-regions-index.json";
import {
  BasemapId,
  BASEMAP_BY_ID,
  DEFAULT_BASEMAP_ID,
  getBasemapProvider,
  isDarkBasemap,
  resolveBasemapId,
} from "../../data/basemaps";

/** Hard cap on DOM markers after viewport cull — keeps pan/zoom responsive. */
const MAX_VISIBLE_MARKERS = 220;
/** Tighter cap on activity browse (trail etc. are dense Europe-wide). */
const MAX_ACTIVITY_BROWSE_MARKERS = 100;

const REGION_COUNT_BY_COUNTRY: Record<string, number> = (() => {
  const counts: Record<string, number> = {};
  for (const r of regionIndex as Array<{ countrySlug: string }>) {
    if (!r?.countrySlug) continue;
    counts[r.countrySlug] = (counts[r.countrySlug] || 0) + 1;
  }
  return counts;
})();

const TERRAIN_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}";

type MapOverlaysState = {
  terrain: boolean;
  forest: boolean;
  rivers: boolean;
};

const DEFAULT_OVERLAYS: MapOverlaysState = {
  terrain: false,
  forest: false,
  rivers: false,
};

/** Forest cover % → fill (choropleth). Avoid pale/white washes. */
function forestDensityFill(density: number, hover = false): string {
  const d = Number(density) || 0;
  const a = hover ? 0.5 : 0.36;
  if (d < 10) return `rgba(76, 140, 70, ${a * 0.35})`;
  if (d < 25) return `rgba(67, 140, 60, ${a})`;
  if (d < 40) return `rgba(46, 125, 50, ${a})`;
  if (d < 55) return `rgba(27, 110, 45, ${a})`;
  return `rgba(15, 80, 30, ${a})`;
}

export interface MapState {
  center: [number, number];
  zoom: number;
}

export interface MapContainerProps {
  mapState: MapState;
  setMapState: React.Dispatch<React.SetStateAction<MapState>>;
  selectedLocation?: {
    lat: number;
    lng: number;
    zoom?: number;
    sectionId?: string | null;
  };
  resetToDefault?: boolean;
}
const filterOptions = [
  { key: "highlights", label: "Orientation", color: "#888888", icon: "others" },
  { key: "cities", label: "Cities", color: "#4a5568", icon: "places" },
  { key: "scotland", label: "Scotland", color: "#005319", icon: "mountains" },
  { key: "europeRest", label: "Rest of Europe", color: "#888888", icon: "places" },
  { key: "posts", label: "Places", color: "black", icon: "places" },
  { key: "beach", label: "Beach", color: "orange", icon: "beach" },
  { key: "climbing", label: "Climbing", color: "brown", icon: "climbing" },
  { key: "ferrata", label: "Ferrata", color: "#8B4513", icon: "via-ferrata" },
  { key: "rafting", label: "Rafting", color: "#1E90FF", icon: "lake" },
  { key: "paragliding", label: "Paragliding", color: "#6A5ACD", icon: "viewpoint" },
  { key: "trail", label: "Trails", color: "#2E8B57", icon: "hiking" },
  { key: "forest", label: "Forest", color: "#228B22", icon: "nature-reserve" },
  { key: "biking", label: "Biking", color: "DarkGreen", icon: "biking" },
  { key: "parking", label: "Airport", color: "red", icon: "airport" },
  { key: "park", label: "Park", color: "DarkOliveGreen", icon: "park" },
  { key: "mountain", label: "Mountain", color: "DarkSlateGrey", icon: "mountains" },
  { key: "camping", label: "Camping", color: "DarkGrey", icon: "camping" },
  { key: "lake", label: "Lake", color: "blue", icon: "lake" },
  { key: "others", label: "Others", color: "gray", icon: "others" },
  { key: "categories", label: "Categories", color: "purple", icon: "marker" },
];

const europeHighlights = getEuropeHighlights();
const curatedEuropeHighlights = getCuratedEuropeHighlights();

const MapContainer: React.FC<MapContainerProps> = ({
  mapState,
  setMapState,
  selectedLocation,
  resetToDefault = false,
}) => {
  const { exploreRadiusKm } = useLocation();
  const routerLocation = useRouterLocation();
  const currentPath =
    routerLocation?.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  const currentSearch = routerLocation?.search || "";

  /** Browse-by-activity mode: /activity/ and /activity/{slug}/?also=&diff=… */
  const activityBrowse = useMemo(() => {
    const parts = (currentPath || "").split("/").filter(Boolean);
    if (parts[0] !== "activity") return null;
    const primary =
      parts[1] && (HIGHLIGHT_ACTIVITIES as string[]).includes(parts[1])
        ? (parts[1] as HighlightActivity)
        : null;
    const alsoRaw = new URLSearchParams(currentSearch).get("also") || "";
    const also = alsoRaw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(
        (s): s is HighlightActivity =>
          Boolean(s) &&
          s !== primary &&
          (HIGHLIGHT_ACTIVITIES as string[]).includes(s)
      );
    const activities: HighlightActivity[] = [];
    if (primary) {
      activities.push(primary, ...also);
    }
    const diffs = parseDifficultyQuery(currentSearch, primary);
    return { primary, activities, diffs };
  }, [currentPath, currentSearch]);

  const isActivityBrowse = Boolean(activityBrowse);

  // Live camera stays local; marker set + parent mapState refresh on settle
  const { view, settled, isMoving, onBoundsChanged, liveZoomRef } =
    useSettledMapView(mapState, setMapState, isActivityBrowse ? 260 : 220);
  const { center, zoom } = view;
  const markerZoom = settled.zoom;
  // Overlays use settled zoom so wheel zoom doesn't flicker layers every tick
  const overlayZoom = settled.zoom;
  const markerBounds = useMemo(
    () => (settled.bounds ? padBounds(settled.bounds, 0.18) : null),
    [settled.bounds]
  );
  const [basemapId, setBasemapId] = useState<BasemapId>(DEFAULT_BASEMAP_ID);
  const [radarTileUrl, setRadarTileUrl] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredMarker, setHoveredMarker] = useState<any>(null);
  const [hoveredCountrySlug, setHoveredCountrySlug] = useState<string | null>(null);
  const [hoveredRegionSlug, setHoveredRegionSlug] = useState<string | null>(null);
  const countryHoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const regionHoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filters, setFilters] = useState<{ [key: string]: boolean }>({
    highlights: true,
    cities: true,
    scotland: true,
    europeRest: true,
    posts: true,
    beach: true,
    climbing: true,
    ferrata: true,
    rafting: true,
    paragliding: true,
    trail: true,
    forest: true,
    biking: true,
    parking: true,
    park: true,
    mountain: true,
    camping: true,
    lake: true,
    others: true,
    categories: true,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [overlays, setOverlays] = useState<MapOverlaysState>(DEFAULT_OVERLAYS);
  const [attributionOpen, setAttributionOpen] = useState(false);
  const [activeCategorySlug, setActiveCategorySlug] = useState<string | null>(null);
  const [activeRegionSlug, setActiveRegionSlug] = useState<string | null>(null);
  const [activeTagSlug, setActiveTagSlug] = useState<string | null>(null);

  const detailHighlight = useMemo(() => {
    if (!currentPath?.startsWith("/highlights/")) return null;
    return europeHighlights.find((h) => h.path === currentPath) || null;
  }, [currentPath]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("mapOverlays");
      if (raw) {
        setOverlays({ ...DEFAULT_OVERLAYS, ...JSON.parse(raw) });
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setOverlay = useCallback((key: keyof MapOverlaysState, value: boolean) => {
    setOverlays((prev) => {
      const next = { ...prev, [key]: value };
      if (typeof window !== "undefined") {
        localStorage.setItem("mapOverlays", JSON.stringify(next));
      }
      return next;
    });
  }, []);

  // (highlight selection is URL-driven via /highlights/{slug}/)

  // Effect to get active category, region, and tag from URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      let categorySlug: string | null = null;
      let regionSlug: string | null = null;
      let tagSlug: string | null = null;

      // Check for /category/slug/tag/tag-slug or /slug/tag/tag-slug
      const tagIndex = pathParts.indexOf('tag');
      if (tagIndex !== -1 && pathParts.length > tagIndex + 1) {
        tagSlug = pathParts[tagIndex + 1];
        // If tag is present, check if there's a category before it
        if (tagIndex > 0) {
          categorySlug = pathParts[tagIndex - 1];
          if (categorySlug === 'category' && tagIndex > 1) {
            categorySlug = pathParts[tagIndex - 2];
          }
        }
      } else if (pathParts.length > 0) {
        // Check for /category/slug or /slug or /country/region
        if (pathParts[0] === 'category' && pathParts.length > 1) {
          categorySlug = pathParts[1];
        } else if (pathParts[0] !== 'tag' && pathParts[0] !== 'page' && pathParts[0] !== 'highlights') {
          categorySlug = pathParts[0];
          // Nested region: /spain/catalonia/ (not a reserved second segment)
          const reserved = new Set(['tag', 'page', 'highlights']);
          if (
            pathParts.length > 1 &&
            !reserved.has(pathParts[1])
          ) {
            regionSlug = pathParts[1];
          }
        }
      }

      setActiveCategorySlug(categorySlug);
      setActiveRegionSlug(regionSlug);
      setActiveTagSlug(tagSlug);
    }
  }, [currentPath]); // Re-run when pathname changes

  // Detect system dark mode only when user has not picked a basemap
useEffect(() => {
  if (typeof window === "undefined") return;
  const saved =
    localStorage.getItem("mapBasemap") || localStorage.getItem("mapStyle");
  if (saved) {
    setBasemapId(resolveBasemapId(saved));
    return;
  }

  const darkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const updateMapStyle = (isDark: boolean) => {
    setBasemapId(isDark ? "night" : "standard");
  };
  updateMapStyle(darkMediaQuery.matches);
  const changeHandler = (e: MediaQueryListEvent) => updateMapStyle(e.matches);
  darkMediaQuery.addEventListener("change", changeHandler);
  return () => darkMediaQuery.removeEventListener("change", changeHandler);
}, []);

  // Live rain radar frame for Weather basemap
  useEffect(() => {
    if (basemapId !== "weather") {
      setRadarTileUrl(null);
      return;
    }
    let cancelled = false;
    const load = () => {
      fetch("https://api.rainviewer.com/public/weather-maps.json")
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          const past = d?.radar?.past || [];
          const last = past[past.length - 1];
          if (d?.host && last?.path) {
            setRadarTileUrl(
              `${d.host}${last.path}/256/{z}/{x}/{y}/2/1_1.png`
            );
          }
        })
        .catch(() => {
          if (!cancelled) setRadarTileUrl(null);
        });
    };
    load();
    const id = window.setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [basemapId]);


  // Load user location from localStorage
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      try {
        setUserLocation(JSON.parse(storedLocation));
      } catch (err) {
        console.error("Error parsing user location from localStorage", err);
      }
    }
    setLoading(false);
  }, []);

  // Reset map to default if requested
  useEffect(() => {
    if (resetToDefault) setMapState({ center: [50.879, 2.6997], zoom: 3 });
  }, [resetToDefault, setMapState]);

  // Center on selected location: pan always; zoom only when explicitly requested
  // (country/region frames). Point pages omit zoom → keep current level.
  const lastFocusRef = useRef<string>("");
  useEffect(() => {
    const lat = Number(selectedLocation?.lat);
    const lng = Number(selectedLocation?.lng);
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      Math.abs(lat) > 85.05 ||
      Math.abs(lng) > 180
    ) {
      return;
    }
    const hasZoom = Number.isFinite(Number(selectedLocation?.zoom));
    const requestedZoom = hasZoom
      ? Math.min(18, Math.max(1, Number(selectedLocation!.zoom)))
      : null;
    const key = `${lat.toFixed(5)},${lng.toFixed(5)},${requestedZoom ?? "keep"}`;
    if (key === lastFocusRef.current) return;
    lastFocusRef.current = key;
    setMapState((prev) => {
      const live = Number(liveZoomRef.current);
      const prevZoom = Number.isFinite(live)
        ? live
        : Number(prev.zoom) || 10;
      let nextZoom = prevZoom;
      if (requestedZoom != null) {
        nextZoom = requestedZoom;
      } else if (prevZoom < 11) {
        // First open from far out with no zoom hint → point-level framing
        nextZoom = 12;
      }
      return { center: [lat, lng], zoom: nextZoom };
    });
  }, [selectedLocation, setMapState, liveZoomRef]);

  const data = useStaticQuery(graphql`
    query MapPostsAndEventsQuery {
      allWpPost {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
          location {
            mapLatitude
            mapLongitude
          }
          tags {
            nodes {
              slug
            }
          }
        }
      }
      allWpEvent {
        nodes {
          id
          title
          uri
          location {
            mapLatitude
            mapLongitude
          }
        }
      }
      allWpPoint {
        nodes {
          id
          title
          slug
          uri
          excerpt
          customAttributes {
            title
            value
          }
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
          pointType {
            nodes {
              slug
              name
            }
          }
          location {
            mapLatitude
            mapLongitude
          }
          tags {
            nodes {
              slug
              name
            }
          }
          featuredImage {
            node {
              sourceUrl
              altText
            }
          }
        }
      }
      allWpCategory {
        nodes {
          slug
          databaseId
          parent {
            id
          }
          parentDatabaseId
          parentId
          name
          description
          location {
            mapLatitude
            mapLongitude
          }
          uri
          link
          categoryId
        }
      }
    }
  `);

  const handleMarkerClick = useCallback(
    (lat: number, lng: number, url: string) => {
      if (currentPath === url) {
        // If the clicked marker is already the active one, do nothing.
        return;
      }
      setHoveredMarker(null);
      // Pan immediately; keep the live zoom (parent may still be mid-settle)
      const liveZoom = liveZoomRef.current;
      setMapState((prev) => ({
        ...prev,
        center: [lat, lng],
        zoom: Number.isFinite(liveZoom) ? liveZoom : prev.zoom,
      }));
      navigate(url);
    },
    [setMapState, currentPath, liveZoomRef]
  );

  const handleMarkerMouseOver = useCallback((node: any) => setHoveredMarker(node), []);
  const handleMarkerMouseOut = useCallback(() => setHoveredMarker(null), []);

  const handleUseMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: [number, number] = [latitude, longitude];
          setUserLocation(location);
          setMapState({ center: location, zoom: 10 });
          localStorage.setItem("userLocation", JSON.stringify(location));
        },
        (error) => console.error("Error fetching user location", error)
      );
    }
  }, [setMapState]);

const allCategories = data.allWpCategory.nodes;

const getPostUrl = (node: any) => {
  const primaryCategory = node.categories?.nodes?.[0];
  const categoryPath = primaryCategory ? buildCategoryPath(primaryCategory, allCategories) : '';
  let safeUri = node.uri ? node.uri : `/${node.slug}`;
  if (!safeUri.startsWith('/')) safeUri = `/${safeUri}`;
  if (!safeUri.endsWith('/')) safeUri = `${safeUri}/`;
  const cleanedUri = safeUri.replace(/\/point(\/|$)/g, '/').replace(/\/+/g, '/');
  const postUrl = `/${categoryPath}${cleanedUri}`.replace(/\/+/g, '/');
  return postUrl;
};

const getPointUrl = (node: any) => {
  const primaryCategory = node.categories?.nodes?.[0];
  const categoryPath = primaryCategory ? buildCategoryPath(primaryCategory, allCategories) : '';
  let safeUri = node.uri ? node.uri : `/${node.slug}`;
  if (!safeUri.startsWith('/')) safeUri = `/${safeUri}`;
  if (!safeUri.endsWith('/')) safeUri = `${safeUri}/`;
  const cleanedUri = safeUri.replace(/\/point(\/|$)/g, '/').replace(/\/+/g, '/');
  const postUrl = `/${categoryPath}${cleanedUri}`.replace(/\/+/g, '/');
  return postUrl;
};

  const activeNode = useMemo(() => {
    let foundNode = null;
    // Check posts
    for (const node of data.allWpPost.nodes) {
      if (getPostUrl(node) === currentPath) {
        foundNode = { ...node, type: 'post' };
        break;
      }
    }
    // Check points if not found in posts
    if (!foundNode) {
      for (const node of data.allWpPoint.nodes) {
        if (getPointUrl(node) === currentPath) {
          foundNode = { ...node, type: 'point' };
          break;
        }
      }
    }
    return foundNode;
  }, [currentPath, data.allWpPost.nodes, data.allWpPoint.nodes, allCategories]); // Add allCategories to dependencies

  const activeNodeId = activeNode?.id;
  const activeNodeType = activeNode?.type;

  const allMarkers = useMemo(() => {
    const markers = [];
    const highlightMarkers = [];
    const markerCap = isActivityBrowse
      ? MAX_ACTIVITY_BROWSE_MARKERS
      : MAX_VISIBLE_MARKERS;

    // Activity browse: wait for settled bounds so we never mount Europe-wide
    // pins in one frame. Highlight/point pages use estimated bounds immediately
    // after a camera jump (see useSettledMapView) so pins don't vanish.
    if (!markerBounds && isActivityBrowse) {
      return [];
    }

    // Orientation + city pins from zoom ≥ 5 (country overview).
    const showOrientation = isActivityBrowse
      ? Boolean(activityBrowse?.activities?.length)
      : markerZoom >= 5 && filters.highlights;
    const showCities =
      !isActivityBrowse && markerZoom >= 5 && filters.cities !== false;

    // Cull when we have bounds. If bounds are missing, still build markers
    // (capped) so highlight pages never show an empty radius ring.
    const cullToViewport = Boolean(markerBounds);

    if (showOrientation || showCities) {
      const activitySet =
        isActivityBrowse && activityBrowse?.activities?.length
          ? new Set(activityBrowse.activities.map((a) => a.toLowerCase()))
          : null;

      // Curated (~322) only — never walk ~3k world seeds on every rebuild.
      // Seeds for the open country are appended when needed.
      const highlightSource = isActivityBrowse
        ? curatedEuropeHighlights
        : activeCategorySlug
          ? [
              ...curatedEuropeHighlights,
              ...europeHighlights.filter(
                (h) =>
                  h.source === "seed" && h.country === activeCategorySlug
              ),
            ]
          : curatedEuropeHighlights;

      highlightSource.forEach((highlight) => {
        const isCity = isLogisticsHubHighlight(highlight);

        // Viewport cull before creating React children (zoomed-in only)
        if (
          cullToViewport &&
          markerBounds &&
          !anchorInBounds(
            [highlight.latitude, highlight.longitude],
            markerBounds
          )
        ) {
          return;
        }

        if (isActivityBrowse) {
          if (highlight.source === "seed") return;
          if (isCity) return;
          const tags = [
            highlight.primaryType,
            ...(highlight.activities || []),
          ].map((t) => (t || "").toLowerCase());
          if (!activitySet || !tags.some((t) => activitySet.has(t))) return;
          const primary = activityBrowse?.primary;
          const diffs = activityBrowse?.diffs || [];
          if (
            primary &&
            diffs.length &&
            !highlightMatchesDifficulty(highlight, primary, diffs)
          ) {
            return;
          }
        } else {
          if (isCity) {
            if (!showCities) return;
          } else if (!showOrientation) {
            return;
          }
          // On a country page, only show that country's orientation pins
          if (
            activeCategorySlug &&
            WORLD_COUNTRIES.some((c) => c.slug === activeCategorySlug) &&
            highlight.country !== activeCategorySlug
          ) {
            return;
          }
          const isScotland = highlight.country === "scotland";
          if (isScotland && !filters.scotland) return;
          if (!isScotland && !filters.europeRest) return;

          // City bases use the Cities layer — don't also require activity toggles
          if (!isCity) {
            const activityVisible = highlight.activities.some(
              (activity) => filters[activity] !== false
            );
            if (!activityVisible) return;
          }
        }

        const isActive = currentPath === highlight.path;
        const isCurrentRegion =
          !isActivityBrowse &&
          Boolean(activeRegionSlug) &&
          (resolveHighlightRegionSlug(
            activeCategorySlug || highlight.country,
            highlight.region
          ) === activeRegionSlug ||
            slugifyRegion(highlight.region) === activeRegionSlug);
        const size = isActive || isCurrentRegion ? 24 : isActivityBrowse ? 18 : 16;
        const hoverNode = {
          title: `${highlight.name} (unverified)`,
          name: highlight.name,
          summary: highlight.summary,
          location: {
            mapLatitude: String(highlight.latitude),
            mapLongitude: String(highlight.longitude),
          },
        };

        highlightMarkers.push({
          id: `highlight-${highlight.id}`,
          type: isCity ? "city" : "highlight",
          isActive,
          isCurrent: isActive || isCurrentRegion,
          priority: highlight.priority || 0,
          anchor: [highlight.latitude, highlight.longitude] as [number, number],
          width: size,
          height: size,
          // 0 is intentional (under points=1, posts=2). Do not use || — 0 is falsy.
          zIndex: isActive || isCurrentRegion ? 1000 : isCity ? 1 : 0,
          onClick: () =>
            handleMarkerClick(highlight.latitude, highlight.longitude, highlight.path),
          onMouseOver: () => handleMarkerMouseOver(hoverNode),
          onMouseOut: handleMarkerMouseOut,
          children: (
            <HighlightMarker
              activities={highlight.activities}
              title={`${highlight.name} (unverified)`}
              isSelected={isActive || isCurrentRegion}
              onClick={() =>
                handleMarkerClick(highlight.latitude, highlight.longitude, highlight.path)
              }
              onMouseOver={() => handleMarkerMouseOver(hoverNode)}
              onMouseOut={handleMarkerMouseOut}
            />
          ),
        });
      });
    }

    if (!isActivityBrowse && markerZoom >= 5 && filters.posts) {
      data.allWpPost.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;
        const lat = parseFloat(mapLatitude);
        const lng = parseFloat(mapLongitude);
        if (cullToViewport && markerBounds && !anchorInBounds([lat, lng], markerBounds)) return;

        const postCategorySlugs = node.categories?.nodes?.map((cat: any) => cat.slug) || [];
        const postTagSlugs = node.tags?.nodes?.map((tag: any) => tag.slug) || [];

        let isCurrentCategory = false;
        if (activeCategorySlug) {
          isCurrentCategory = postCategorySlugs.includes(activeCategorySlug);
        }

        let isCurrentTag = true;
        if (activeTagSlug) {
          isCurrentTag = postTagSlugs.includes(activeTagSlug);
        }

        const isCategoryOrTagSelected = isCurrentCategory && isCurrentTag;
        const isActive = node.id === activeNodeId;

        markers.push({
          id: node.id,
          type: 'post',
          isActive,
          isCurrent: isCategoryOrTagSelected || isActive,
          anchor: [lat, lng],
          width: isCategoryOrTagSelected ? 28 : 16,
          zIndex: isActive ? 1000 : 2,
          onClick: () => handleMarkerClick(lat, lng, getPostUrl(node)),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
          children: (
            <PostMarker
              onClick={() => handleMarkerClick(lat, lng, getPostUrl(node))}
              onMouseOver={() => handleMarkerMouseOver(node)}
              onMouseOut={handleMarkerMouseOut}
              title={node.title}
              isCategoryOrTagSelected={isCategoryOrTagSelected}
              isActive={isActive}
            />
          ),
        });
      });
    }

    if (!isActivityBrowse && markerZoom >= 5) {
      data.allWpEvent.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;
        const lat = parseFloat(mapLatitude);
        const lng = parseFloat(mapLongitude);
        if (cullToViewport && markerBounds && !anchorInBounds([lat, lng], markerBounds)) return;

        markers.push({
          id: node.id,
          type: 'event',
          isCurrent: false,
          anchor: [lat, lng],
          width: 33,
          color: '#d90d0d',
          onClick: () => handleMarkerClick(lat, lng, node.uri),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
        });
      });
    }

    // WP points — activity-filtered in browse; else zoom ≥ 5
    if (isActivityBrowse || markerZoom >= 5) {
      data.allWpPoint.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;
        const lat = parseFloat(mapLatitude);
        const lng = parseFloat(mapLongitude);
        if (cullToViewport && markerBounds && !anchorInBounds([lat, lng], markerBounds)) return;

        const pointCategorySlugs = node.categories?.nodes?.map((cat: any) => cat.slug) || [];
        const pointTagSlugs = node.tags?.nodes?.map((tag: any) => tag.slug) || [];

        let isCurrentCategory = false;
        if (activeCategorySlug) {
          isCurrentCategory = pointCategorySlugs.includes(activeCategorySlug);
        }

        let isCurrentTag = true;
        if (activeTagSlug) {
          isCurrentTag = pointTagSlugs.includes(activeTagSlug);
        }

        const isCategoryOrTagSelected = isCurrentCategory && isCurrentTag;
        const isActive = node.id === activeNodeId;

        const types = node.pointType?.nodes?.map((t: any) => t.slug.toLowerCase()) || [];
        const knownTypes = ["beach", "climbing", "biking", "park", "mountains", "camping", "airport", "lake"];
        const isBeach = types.includes("beach");
        const isParking = types.includes("airport");
        const isClimbing = types.includes("climbing");
        const isPark = types.includes("park");
        const isMountain = types.includes("mountains");
        const isCamping = types.includes("camping");
        const isLake = types.includes("lake");
        const isBiking = types.includes("biking");
        const isKnown = types.some((t) => knownTypes.includes(t));

        if (isActivityBrowse) {
          const acts = activityBrowse?.activities || [];
          if (!acts.length) return;
          const matchesAct = acts.some((a) =>
            pointMatchesActivity(node as WpActivityPoint, a)
          );
          if (!matchesAct) return;
          const primary = activityBrowse?.primary;
          const diffs = activityBrowse?.diffs || [];
          if (primary && diffs.length) {
            const asHighlight = getWpHighlightsForActivities(
              [node as WpActivityPoint],
              [primary]
            )[0];
            if (
              !asHighlight ||
              !highlightMatchesDifficulty(asHighlight, primary, diffs)
            ) {
              return;
            }
          }
        } else if (
          (isBeach && !filters.beach) ||
          (isParking && !filters.parking) ||
          (isClimbing && !filters.climbing) ||
          (isBiking && !filters.biking) ||
          (isPark && !filters.park) ||
          (isMountain && !filters.mountain) ||
          (isCamping && !filters.camping) ||
          (isLake && !filters.lake) ||
          (isBiking && !filters.biking) ||
          (!isKnown && !filters.others)
        ) {
          return;
        }

        const color = isBeach
          ? "orange"
          : isParking
          ? "darkred"
          : isClimbing
          ? "brown"
          : isBiking
          ? "DarkGreen"
          : isPark
          ? "DarkOliveGreen"
          : isMountain
          ? "DarkSlateGrey"
          : isCamping
          ? "DarkGrey"
          : isLake
          ? "blue"
          : "gray";

        const currentPointType = isBeach
          ? "beach"
          : isParking
          ? "airport"
          : isClimbing
          ? "climbing"
          : isBiking
          ? "biking"
          : isPark
          ? "park"
          : isMountain
          ? "mountains"
          : isCamping
          ? "camping"
          : isLake
          ? "lake"
          : "others";

        markers.push({
          id: node.id,
          type: 'point',
          isActive,
          isCurrent: isCategoryOrTagSelected || isActive,
          priority: 0,
          anchor: [lat, lng],
          width: isCategoryOrTagSelected ? 28 : 16,
          color,
          zIndex: isActive ? 1000 : 1,
          onClick: () => handleMarkerClick(lat, lng, getPointUrl(node)),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
          children: (
            <PointMarker
              type={currentPointType}
              onClick={() => handleMarkerClick(lat, lng, getPointUrl(node))}
              onMouseOver={() => handleMarkerMouseOver(node)}
              onMouseOut={handleMarkerMouseOut}
              title={node.title}
              isCategoryOrTagSelected={isCategoryOrTagSelected}
              isActive={isActive}
            />
          ),
        });
      });
    }

    // Verified WP markers on top; curated gray highlights stay underneath
    const verified = markers.sort((a, b) => (a.isCurrent ? 1 : -1));
    const combined = [...highlightMarkers, ...verified];

    // Final viewport filter only when densely zoomed; overview uses markerCap rank
    const inView =
      isActivityBrowse || !markerBounds || !cullToViewport
        ? combined
        : combined.filter((m) => anchorInBounds(m.anchor, markerBounds));

    if (inView.length <= markerCap) return inView;

    // Never drop the active pin (e.g. clicked city) — keep it even under the cap
    const mustKeep = inView.filter(
      (m) => (m as { isActive?: boolean }).isActive
    );
    const rest = inView.filter((m) => !(m as { isActive?: boolean }).isActive);
    // Prefer active + nearby Italy when ranking under the cap
    const ranked = [...rest].sort((a, b) => {
      const score = (m: typeof a) =>
        (m.isCurrent ? 8 : 0) +
        ((m as { isActive?: boolean }).isActive ? 12 : 0) +
        (m.type === "post"
          ? 3
          : m.type === "point"
            ? 2
            : m.type === "city"
              ? 4
              : m.type === "event"
                ? 1
                : m.type === "highlight"
                  ? 2
                  : 0) +
        Math.min(3, (m as { priority?: number }).priority || 0);
      return score(b) - score(a);
    });
    const slots = Math.max(0, markerCap - mustKeep.length);
    return [...mustKeep, ...ranked.slice(0, slots)];

  }, [
    markerZoom,
    markerBounds,
    filters,
    activeCategorySlug,
    activeRegionSlug,
    activeTagSlug,
    data,
    currentPath,
    isActivityBrowse,
    activityBrowse,
    handleMarkerClick,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
  ]);
  const wpCategoryBySlug = useMemo(() => {
    const map: Record<string, any> = {};
    data.allWpCategory.nodes.forEach((node: any) => {
      if (node?.slug) map[node.slug.toLowerCase()] = node;
    });
    return map;
  }, [data.allWpCategory.nodes]);

  const activeCountrySlug = useMemo(() => {
    if (!currentPath) return null;
    const first = currentPath.split("/").filter(Boolean)[0];
    if (!first) return null;
    if (WORLD_COUNTRIES.some((c) => c.slug === first)) return first;
    if (wpCategoryBySlug[first] && !wpCategoryBySlug[first].parentDatabaseId) {
      return first;
    }
    return null;
  }, [currentPath, wpCategoryBySlug]);

  /** Normalize WP underscore slugs to match GeoJSON regionSlug (hyphenated). */
  const mapActiveRegionSlug = useMemo(() => {
    if (!activeRegionSlug) return null;
    return activeRegionSlug.toLowerCase().replace(/_/g, "-");
  }, [activeRegionSlug]);

  const handleCountrySelect = useCallback(
    (slug: string, lat: number, lng: number) => {
      handleMarkerClick(lat, lng, `/${slug}/`);
    },
    [handleMarkerClick]
  );

  const handleRegionSelect = useCallback(
    (region: {
      countrySlug: string;
      regionSlug: string;
      name: string;
      lat: number;
      lng: number;
    }) => {
      const countryCat = data.allWpCategory.nodes.find(
        (c: any) => c.slug === region.countrySlug && !c.parentDatabaseId
      );
      const slugVariants = [
        region.regionSlug,
        region.regionSlug.replace(/-/g, "_"),
      ];
      let path = `/${region.countrySlug}/${region.regionSlug}/`;
      if (countryCat) {
        for (const slug of slugVariants) {
          const child = data.allWpCategory.nodes.find(
            (c: any) =>
              c.slug === slug && c.parentDatabaseId === countryCat.databaseId
          );
          if (child) {
            path = `/${region.countrySlug}/${child.slug}/`;
            break;
          }
        }
      }
      // Prefer ADM1 centroid so we don't keep the country frame while navigating
      const zoom = Math.min(14, (mapState.zoom || 5) + 1.5);
      if (currentPath === path) return;
      setMapState({ center: [region.lat, region.lng], zoom });
      setHoveredMarker(null);
      navigate(path);
    },
    [
      data.allWpCategory.nodes,
      mapState.zoom,
      currentPath,
      setMapState,
    ]
  );

  const handleRegionShapeEnter = useCallback(
    (region: {
      countrySlug: string;
      regionSlug: string;
      name: string;
      lat: number;
      lng: number;
    }) => {
      if (regionHoverLeaveTimer.current) {
        clearTimeout(regionHoverLeaveTimer.current);
        regionHoverLeaveTimer.current = null;
      }
      // Region owns the focus ring — drop country hover stroke
      if (countryHoverLeaveTimer.current) {
        clearTimeout(countryHoverLeaveTimer.current);
        countryHoverLeaveTimer.current = null;
      }
      setHoveredCountrySlug(null);
      setHoveredRegionSlug((prev) =>
        prev === region.regionSlug ? prev : region.regionSlug
      );
      handleMarkerMouseOver({
        title: region.name,
        name: region.name,
        location: {
          mapLatitude: String(region.lat),
          mapLongitude: String(region.lng),
        },
      });
    },
    [handleMarkerMouseOver]
  );

  const handleRegionShapeLeave = useCallback(() => {
    if (regionHoverLeaveTimer.current) {
      clearTimeout(regionHoverLeaveTimer.current);
    }
    regionHoverLeaveTimer.current = setTimeout(() => {
      setHoveredRegionSlug(null);
      handleMarkerMouseOut();
      regionHoverLeaveTimer.current = null;
    }, 180);
  }, [handleMarkerMouseOut]);

  const handleCountryFlagEnter = useCallback(
    (country: { slug: string; name: string; lat: number; lng: number }) => {
      if (countryHoverLeaveTimer.current) {
        clearTimeout(countryHoverLeaveTimer.current);
        countryHoverLeaveTimer.current = null;
      }
      setHoveredCountrySlug((prev) => (prev === country.slug ? prev : country.slug));
      handleMarkerMouseOver({
        title: country.name,
        name: country.name,
        location: {
          mapLatitude: String(country.lat),
          mapLongitude: String(country.lng),
        },
      });
    },
    [handleMarkerMouseOver]
  );

  const handleCountryShapeEnter = useCallback(
    (country: { slug: string; name: string; lat: number; lng: number }) => {
      if (countryHoverLeaveTimer.current) {
        clearTimeout(countryHoverLeaveTimer.current);
        countryHoverLeaveTimer.current = null;
      }
      // Leaving the active country for a neighbor — drop region hover so
      // country hover ring can show clearly
      setHoveredRegionSlug(null);
      // Avoid re-render loops when moving inside the same country (MultiPolygon gaps)
      setHoveredCountrySlug((prev) => (prev === country.slug ? prev : country.slug));
    },
    []
  );

  const handleCountryFlagLeave = useCallback(() => {
    if (countryHoverLeaveTimer.current) {
      clearTimeout(countryHoverLeaveTimer.current);
    }
    countryHoverLeaveTimer.current = setTimeout(() => {
      setHoveredCountrySlug(null);
      handleMarkerMouseOut();
      countryHoverLeaveTimer.current = null;
    }, 200);
  }, [handleMarkerMouseOut]);

  const handleCountryShapeLeave = useCallback(() => {
    if (countryHoverLeaveTimer.current) {
      clearTimeout(countryHoverLeaveTimer.current);
    }
    // Longer debounce: gaps between Finland’s polygons must not clear hover
    countryHoverLeaveTimer.current = setTimeout(() => {
      setHoveredCountrySlug(null);
      countryHoverLeaveTimer.current = null;
    }, 220);
  }, []);

  useEffect(
    () => () => {
      if (countryHoverLeaveTimer.current) {
        clearTimeout(countryHoverLeaveTimer.current);
      }
      if (regionHoverLeaveTimer.current) {
        clearTimeout(regionHoverLeaveTimer.current);
      }
    },
    []
  );

  const clearCountryHover = useCallback(() => {
    if (countryHoverLeaveTimer.current) {
      clearTimeout(countryHoverLeaveTimer.current);
      countryHoverLeaveTimer.current = null;
    }
    setHoveredCountrySlug(null);
    handleMarkerMouseOut();
  }, [handleMarkerMouseOut]);

  const renderMarkersCategory = () => {
    // Activity browse: hide country flags so pins stay the focus
    if (isActivityBrowse) {
      return null;
    }
    if (markerZoom > 5 || !filters.categories) {
      return null;
    }

    // Pixel size by zoom — small at world view, larger near country level
    // (keep px, not em, so Leaflet zoom transforms don't distort them)
    const z = markerZoom || 2;
    const flagSize =
      z <= 2 ? 14 : z <= 2.5 ? 16 : z <= 3 ? 18 : z <= 3.5 ? 20 : z <= 4 ? 24 : 28;

    return WORLD_COUNTRIES.map((country) => {
      if (!hasFlagAsset(country.slug, country.iso2)) {
        return null;
      }

      const wp = wpCategoryBySlug[country.slug];
      const wpLat = wp?.location?.mapLatitude
        ? parseFloat(wp.location.mapLatitude)
        : null;
      const wpLng = wp?.location?.mapLongitude
        ? parseFloat(wp.location.mapLongitude)
        : null;
      const lat = Number.isFinite(wpLat) ? (wpLat as number) : country.lat;
      const lng = Number.isFinite(wpLng) ? (wpLng as number) : country.lng;

      if (markerBounds && !anchorInBounds([lat, lng], markerBounds)) {
        return null;
      }

      return (
        <Marker
          key={`country-${country.iso2}-${country.slug}`}
          anchor={[lat, lng]}
          width={flagSize}
          height={flagSize}
          onClick={() => handleCountrySelect(country.slug, lat, lng)}
          onMouseOver={() =>
            handleCountryFlagEnter({
              slug: country.slug,
              name: country.name,
              lat,
              lng,
            })
          }
          onMouseOut={handleCountryFlagLeave}
        >
          <CountryFlagMarker
            countryCode={country.slug}
            iso2={country.iso2}
            name={country.name}
            size={flagSize}
            isSelected={activeCountrySlug === country.slug}
            onClick={() => handleCountrySelect(country.slug, lat, lng)}
            onMouseOver={() =>
              handleCountryFlagEnter({
                slug: country.slug,
                name: country.name,
                lat,
                lng,
              })
            }
            onMouseOut={handleCountryFlagLeave}
          />
        </Marker>
      );
    });
  };

  if (loading) {return (
      <div className="loadingContainer">
        <div className="spinner"></div>
        <span className="loadingText">Loading map...</span>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }} onMouseLeave={clearCountryHover}>
      <button
        className="location-button"
        tabIndex={-1}
        title="Get your location"
        aria-label="Get your location"
        onClick={handleUseMyLocation}
      >
        <span>My location</span>
      </button>

      <MapStyleSwitcher onBasemapChange={setBasemapId} />

      <div
        style={{ position: "absolute", top: "10px", left: "10px", zIndex: 1000 }}
        onMouseLeave={() => setFilterOpen(false)}
      >
        <button
          onClick={() => setFilterOpen((prev) => !prev)}
          style={{
            background: "rgba(255,255,255,0.9)",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          {filterOpen ? "Close ✖" : "Layers ☰"}
        </button>
        {filterOpen && (
          <div
            style={{
              marginTop: "6px",
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "8px",
              borderRadius: "8px",
              fontSize: "12px",
              display: "grid",
              justifyItems: "start",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#333" }}>
              Map overlays
            </div>
            {(
              [
                { key: "terrain" as const, label: "Terrain", color: "#555" },
                { key: "forest" as const, label: "Forest", color: "#228B22" },
                { key: "rivers" as const, label: "Rivers", color: "#1E6BB8" },
              ] as const
            ).map(({ key, label, color }) => (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={overlays[key]}
                  onChange={(e) => setOverlay(key, e.target.checked)}
                  style={{ accentColor: color }}
                />
                <span style={{ color, marginLeft: 4 }}>{label}</span>
              </label>
            ))}
            <div
              style={{
                fontWeight: 600,
                margin: "8px 0 6px",
                color: "#333",
                borderTop: "1px solid #ddd",
                paddingTop: 6,
                width: "100%",
              }}
            >
              Pins
            </div>
            {filterOptions.map(({ key, label, color, icon }) => (
              <label key={key} style={{ display: "flex", alignItems: "center", marginBottom: 4, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={filters[key]}
                  onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                  style={{ accentColor: color }}
                />
                <img src={`/images/icons/${icon}.svg`} alt="" style={{ width: "30px", paddingRight: "0px"}}/><span style={{ color, marginLeft: 4 }}>{label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <Map
        provider={getBasemapProvider(basemapId)}
        dprs={[1, 2]}
        center={center}
        zoom={zoom}
        zoomSnap={false}
        animate
        // Snap large programmatic jumps (fresh Lucerne load) — no Europe→city fly-in
        animateMaxScreens={0.45}
        touchEvents
        twoFingerDrag={false}
        onBoundsChanged={onBoundsChanged}
        style={{ width: "100%", minHeight: "500px" }}
      >
        {basemapId === "weather" && radarTileUrl && (
          <MapTileOverlay
            urlTemplate={radarTileUrl}
            opacity={0.65}
            blendMode="screen"
            maxNativeZoom={12}
            minZoom={2}
            zIndex={2}
          />
        )}
        {overlays.terrain && zoom >= 4 && zoom <= 14 && (
          <MapTileOverlay
            urlTemplate={TERRAIN_TILES}
            opacity={0.7}
            blendMode="multiply"
            maxNativeZoom={12}
            minZoom={4}
            zIndex={1}
          />
        )}
        {overlays.forest && zoom >= 3 && zoom <= 10 && (
          <SafeGeoJsonLoader
            link="/data/europe-forests.geojson"
            styleCallback={(feature, hover) => ({
              fill: forestDensityFill(feature?.properties?.density, hover),
              strokeWidth: hover ? "1.25" : "0.55",
              stroke: hover ? "#1b5e20" : "rgba(27,94,32,0.4)",
              fillOpacity: "1",
              r: "0",
            })}
          />
        )}
        {overlays.rivers && zoom >= 5 && (
          <SafeGeoJsonLoader
            link="/data/europe-rivers.geojson"
            styleCallback={() => ({
              strokeWidth: zoom >= 8 ? "2.5" : "1.75",
              stroke: "#1E6BB8",
              fill: "none",
              r: "0",
            })}
          />
        )}

        {detailHighlight &&
          (detailHighlight.riverLine?.length ||
            detailHighlight.sections?.length) && (
            <HighlightRiverOverlay
              highlight={detailHighlight}
              activeSectionId={selectedLocation?.sectionId || null}
              zoom={zoom}
            />
          )}

        {/* Country outlines — world browse only; country pages use activeOnly ring */}
        {!isActivityBrowse && overlayZoom < 8 && (
          <CountryShapesOverlay
            zoom={overlayZoom}
            hoveredCountrySlug={hoveredCountrySlug}
            activeCountrySlug={activeCountrySlug}
            regionsVisible={
              Boolean(activeCountrySlug) &&
              (REGION_COUNT_BY_COUNTRY[activeCountrySlug] || 0) > 1
            }
            activeRegionSlug={mapActiveRegionSlug}
            hoveredRegionSlug={hoveredRegionSlug}
            darkBasemap={isDarkBasemap(basemapId)}
            viewBounds={markerBounds}
            isMoving={isMoving}
            onCountryClick={handleCountrySelect}
            onCountryEnter={handleCountryShapeEnter}
            onCountryLeave={handleCountryShapeLeave}
            activeOnly={Boolean(activeCountrySlug)}
          />
        )}

        {/* Region divisions — mid zoom only (skip country overview frames like Italy @ 5.3) */}
        {!isActivityBrowse &&
          activeCountrySlug &&
          !isMoving &&
          overlayZoom >= 5.8 &&
          overlayZoom < 9 && (
          <RegionShapesOverlay
            zoom={overlayZoom}
            countrySlug={activeCountrySlug}
            hoveredRegionSlug={hoveredRegionSlug}
            activeRegionSlug={mapActiveRegionSlug}
            onRegionClick={handleRegionSelect}
            onRegionEnter={handleRegionShapeEnter}
            onRegionLeave={handleRegionShapeLeave}
          />
        )}

        {selectedLocation &&
          Number.isFinite(selectedLocation.lat) &&
          Number.isFinite(selectedLocation.lng) &&
          exploreRadiusKm > 0 &&
          // Place / highlight pages only — not country overview
          (currentPath.split("/").filter(Boolean).length >= 2 ||
            currentPath.startsWith("/highlights/")) && (
            <ExploreRadiusOverlay
              lat={selectedLocation.lat}
              lng={selectedLocation.lng}
              radiusKm={exploreRadiusKm}
            />
          )}

        {allMarkers.map((marker) => {
          const { children, id, zIndex, type: _type, isCurrent: _isCurrent, ...markerProps } = marker;
          const isHighlight =
            marker.type === "highlight" || marker.type === "city";
          return (
            <Marker
              key={id}
              {...markerProps}
              style={{
                // Preserve zIndex 0 for orientation pins under verified markers
                zIndex: zIndex ?? 1,
                // pigeon-maps defaults to pointerEvents:none; highlights must receive clicks
                ...(isHighlight ? { pointerEvents: "auto" } : {}),
                // Soften while panning — DOM stays until settle refresh
                ...(isMoving ? { opacity: 0.72 } : {}),
              }}
            >
              {children}
            </Marker>
          );
        })}

        {filters.categories && renderMarkersCategory()}

        {userLocation && <Marker anchor={userLocation} width={50} hover={false} color="lightblue" style={{ zIndex: 999 }} onClick={() => navigate("/places-around-me/")} />}

        {hoveredMarker && hoveredMarker.location?.mapLatitude && hoveredMarker.location?.mapLongitude && (
          <Overlay
            anchor={[
              parseFloat(hoveredMarker.location.mapLatitude),
              parseFloat(hoveredMarker.location.mapLongitude),
            ]}
            offset={[0, -10]}
            zIndex={9999}
          >
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "5px",
                borderRadius: "5px",
                maxWidth: "200px",
              }}
            >
              {hoveredMarker.title && hoveredMarker.title.trim() !== '' ? hoveredMarker.title.split(":")[0] : hoveredMarker.name}
            </div>
          </Overlay>
        )}

        <ZoomControl style={{ bottom: "10px", top: "unset", zoom: "1.4" }} />
      </Map>

      {(basemapId !== "standard" ||
        overlays.terrain ||
        overlays.forest ||
        overlays.rivers) && (
        <div className="map-attribution">
          {attributionOpen ? (
            <>
              <span className="map-attribution-text">
                {[
                  BASEMAP_BY_ID[basemapId]?.attribution,
                  overlays.terrain && "Esri Hillshade",
                  overlays.forest && "Forest cover",
                  overlays.rivers && "Natural Earth",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>{" "}
              <button
                type="button"
                className="map-attribution-link"
                onClick={() => setAttributionOpen(false)}
              >
                Hide
              </button>
            </>
          ) : (
            <button
              type="button"
              className="map-attribution-link"
              onClick={() => setAttributionOpen(true)}
              aria-expanded={false}
              title="Map data credits"
            >
              ©
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MapContainer;
